<template>
  <div class="def-examples">
    <div
      ref="clampRef"
      class="def-ex-clamp"
      :class="{ 'is-open': expanded, 'is-fade': overflowing && !expanded }"
    >
      <p v-for="(ex, i) in examples" :key="i" class="def-example">{{ ex }}</p>
    </div>
    <button
      v-if="overflowing"
      type="button"
      class="def-ex-toggle"
      data-testid="examples-toggle"
      @click="expanded = !expanded"
    >
      {{ expanded ? STRINGS.definitions.showFewerExamples : STRINGS.definitions.showMoreExamples }}
    </button>
  </div>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref, watch } from 'vue';
import STRINGS from '@/constants/strings';

const props = defineProps<{
  examples: readonly string[];
}>();

// Examples are capped at 2 per sense by the extractor, so a count-based "show N
// more" never fires. Instead the block clamps to a few lines (a CSS max-height)
// and a Show more / Show less toggle appears only when the text actually
// overflows the clamp — a height-based reveal that triggers on length.
const clampRef = ref<HTMLElement | null>(null);
const expanded = ref(false);
const overflowing = ref(false);

// Only meaningful while collapsed: once expanded the clamp is lifted, so
// scrollHeight === clientHeight and the measurement would falsely read "fits".
// Skip it and keep the toggle visible so the reader can collapse again.
function measure(): void {
  const el = clampRef.value;
  if (!el || expanded.value) return;
  overflowing.value = el.scrollHeight - el.clientHeight > 2;
}

let observer: ResizeObserver | null = null;

onMounted(() => {
  measure();
  // Web-font swap can change the wrapped line count after first paint.
  if (typeof document !== 'undefined' && document.fonts?.ready) {
    document.fonts.ready.then(measure);
  }
  if (typeof ResizeObserver !== 'undefined') {
    observer = new ResizeObserver(() => measure());
    if (clampRef.value) observer.observe(clampRef.value);
  }
});

onBeforeUnmount(() => observer?.disconnect());

// Re-measure if the examples change (e.g. switching words reuses the component).
watch(
  () => props.examples,
  () => {
    expanded.value = false;
    measure();
  },
  { flush: 'post' }
);
</script>

<style scoped>
.def-examples {
  width: 100%;
  margin-top: 0.125rem;
  display: flex;
  flex-direction: column;
  gap: 0.3125rem;
}

.def-ex-clamp {
  position: relative;
  overflow: hidden;
  max-height: 4.5em;
  padding-left: 0.6875rem;
  border-left: 2px solid var(--color-arena-border);
  display: flex;
  flex-direction: column;
  gap: 0.3125rem;
}

.def-ex-clamp.is-open {
  max-height: none;
}

/* Soften the hard cut when clamped and there is more below. mask-image is
   background-agnostic, so it works regardless of the surface behind it. */
.def-ex-clamp.is-fade {
  -webkit-mask-image: linear-gradient(to bottom, #000 65%, transparent);
  mask-image: linear-gradient(to bottom, #000 65%, transparent);
}

.def-example {
  margin: 0;
  font-family: var(--font-sans);
  font-style: italic;
  font-size: 0.78125rem;
  line-height: 1.5;
  color: var(--color-arena-text-muted);
}

.def-example::before {
  content: '\201C';
}

.def-example::after {
  content: '\201D';
}

.def-ex-toggle {
  align-self: flex-start;
  padding: 0.125rem 0 0;
  margin: 0;
  font-family: var(--font-sans);
  font-size: 0.71875rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: var(--color-energy-primary);
  background: none;
  border: none;
  cursor: pointer;
}

.def-ex-toggle:hover,
.def-ex-toggle:focus-visible {
  text-decoration: underline;
}
</style>
